import { useEffect, useState } from 'react';
import { Badge, Card, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import { formatDate as formatDay } from '../utils/dateFormat';
const STATUS_BADGE = { ACTIVE: 'success', TRIAL: 'info', EXPIRED: 'danger', SUSPENDED: 'secondary', CANCELLED: 'secondary' };

const formatCurrency = (amount) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `₹${(Number.isFinite(num) ? num : 0).toFixed(2)}`;
};

const formatDate = (value) => (value ? formatDay(value) : '-');

/**
 * The Account Manager's own assigned organizations, with what each has paid so far, its
 * subscription status/renewal, and the commission this Account Manager has earned on it - see
 * AccountManagerFeedbackController's organizations endpoint. Renewal date is shown rather than a
 * rupee "amount due", since there's no billing-cycle-due tracking in the system to source that
 * from honestly.
 */
function AccountManagerOrganizations() {
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    axios.get('/api/account-manager/organizations')
      .then((r) => setOrganizations(r.data?.organizations || []))
      .catch(() => toast.error('Failed to load your organizations'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">Your organizations</h5>
        <p className="text-muted small mb-0 mt-2">Organizations you're currently the Account Manager for.</p>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="d-flex justify-content-center"><Spinner animation="border" /></div>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Plan</th>
                <th>Subscription</th>
                <th>Your terms</th>
                <th>Paid so far</th>
                <th>Commission earned</th>
                <th>Commission paid</th>
                <th>Commission pending</th>
              </tr>
            </thead>
            <tbody>
              {organizations.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-muted">You're not currently assigned to any organization.</td></tr>
              ) : (
                organizations.map((org) => (
                  <tr key={org.id}>
                    <td>{org.name}</td>
                    <td>{org.subscriptionPlanName || '-'}</td>
                    <td>
                      <div><Badge bg={STATUS_BADGE[org.subscriptionStatus] || 'secondary'}>{org.subscriptionStatus || 'UNKNOWN'}</Badge></div>
                      <small className="text-muted">renews {formatDate(org.subscriptionEndDate)}</small>
                    </td>
                    <td>
                      {org.initialRatePercent != null ? (
                        <>
                          <div>{org.initialRatePercent}% for {org.initialPeriodMonths} month{org.initialPeriodMonths === 1 ? '' : 's'}, then {org.trailingRatePercent}%</div>
                          {(org.capAmount != null || org.capMonths != null) && (
                            <small className="text-muted">
                              {org.capAmount != null ? `capped at ${formatCurrency(org.capAmount)}` : ''}
                              {org.capAmount != null && org.capMonths != null ? ', ' : ''}
                              {org.capMonths != null ? `stops after ${org.capMonths} months` : ''}
                            </small>
                          )}
                          <div><small className="text-muted">since {formatDate(org.assignedSince)}</small></div>
                        </>
                      ) : '-'}
                    </td>
                    <td>{formatCurrency(org.paidSoFar)}</td>
                    <td>{formatCurrency(org.commissionEarned)}</td>
                    <td>{formatCurrency(org.commissionPaid)}</td>
                    <td>{formatCurrency(org.commissionPending)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

export default AccountManagerOrganizations;
